import React, { useState, useMemo, useEffect, useRef } from 'react';
import { 
  Plus, Search, Receipt, DollarSign, X, Save, 
  Trash2, Send, 
  ChevronRight, Building, Home, User, Ruler, 
  Calculator, ChevronDown, ChevronUp, Zap, ArrowRight,
  Warehouse, HardHat, Check, UserPlus, Copy, Download,
  Loader2, FileDown,
  ExternalLink,
  ChevronLeft,
  Edit2,
  Briefcase,
  MessageSquareText,
  StickyNote,
  Eye,
  Tag,
  ArrowLeft,
  Percent,
  MapPin,
  Trash,
  Calendar,
  Phone,
  Mail,
  Activity,
  CreditCard,
  Link as LinkIcon,
  ShieldCheck,
  RefreshCw,
  AlertCircle,
  Library,
  MailCheck,
  Settings2,
  SendHorizontal,
  FileText,
  Ban,
  Clock
} from 'lucide-react';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { Invoice, Service, Inspection, Contact, Location, InvoiceItem, BillingSettings, ModifierRates, Discount, BusinessProfile, Note, NoteCategory, FileRecord } from '../types';
import { supabase, toSnakeCase, getErrorMessage, toCamelCase } from '../lib/supabase';

interface InvoicesProps {
  invoices: Invoice[];
  setInvoices: React.Dispatch<React.SetStateAction<Invoice[]>>;
  services: Service[];
  inspections: Inspection[];
  setInspections: React.Dispatch<React.SetStateAction<Inspection[]>>;
  contacts: Contact[];
  locations: Location[];
  initialInspectionId?: string | null;
  onClearDraft?: () => void;
  billingSettings: BillingSettings;
  setBillingSettings: React.Dispatch<React.SetStateAction<BillingSettings>>;
  businessProfile: BusinessProfile;
  notes: Note[];
  setNotes: React.Dispatch<React.SetStateAction<Note[]>>;
  categories: NoteCategory[];
}

const Invoices: React.FC<InvoicesProps> = ({ 
  invoices, 
  setInvoices, 
  services, 
  inspections, 
  setInspections,
  contacts, 
  locations,
  initialInspectionId,
  onClearDraft,
  billingSettings,
  setBillingSettings,
  businessProfile,
  notes,
  setNotes,
  categories
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isDiscountLibraryOpen, setIsDiscountLibraryOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isGeneratingStripe, setIsGeneratingStripe] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);

  // Search & Selector states
  const [servicePickerId, setServicePickerId] = useState('');
  const [contactSearch, setContactSearch] = useState('');
  const [isContactDropdownOpen, setIsContactDropdownOpen] = useState(false);
  const [inspectionSearch, setInspectionSearch] = useState('');
  const [isInspectionDropdownOpen, setIsInspectionDropdownOpen] = useState(false);
  
  // Discount Library Management States
  const [discountLibSaving, setDiscountLibSaving] = useState(false);
  const [discountForm, setDiscountForm] = useState<Partial<Discount>>({ name: '', type: 'Percentage', value: 0 });

  const contactDropdownRef = useRef<HTMLDivElement>(null);
  const inspectionDropdownRef = useRef<HTMLDivElement>(null);
  const pdfTemplateRef = useRef<HTMLDivElement>(null);

  // Click outside handlers
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (contactDropdownRef.current && !contactDropdownRef.current.contains(event.target as Node)) setIsContactDropdownOpen(false);
      if (inspectionDropdownRef.current && !inspectionDropdownRef.current.contains(event.target as Node)) setIsInspectionDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const generateInvoiceNumber = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `INV-${year}${month}-${random}`;
  };

  const [formData, setFormData] = useState<Partial<Invoice>>({
    invoiceNumber: '',
    inspectionId: '',
    propertyId: '',
    contactId: '',
    items: [],
    modifiers: { 
      sqft: 0, 
      distance: 0, 
      basement: false, 
      crawlspace: false,
      applySqftPremium: false,
      applyDistancePremium: false 
    },
    modifierRates: { 
      sqftRate: billingSettings.defaultSqftRate, 
      sqftThreshold: billingSettings.defaultSqftThreshold,
      distanceRate: billingSettings.defaultDistanceRate, 
      distanceThreshold: billingSettings.defaultDistanceThreshold,
      basementFee: billingSettings.defaultBasementFee, 
      crawlspaceFee: billingSettings.defaultCrawlspaceFee 
    },
    discounts: [],
    taxRate: businessProfile.defaultTaxRate || 0,
    status: 'draft'
  });

  const getContact = (id: string) => contacts.find(c => c.id === id);
  const getProperty = (id: string) => locations.find(l => l.id === id);
  const getInspection = (id: string) => inspections.find(i => i.id === id);

  const selectedInvoice = useMemo(() => 
    invoices.find(inv => inv.id === selectedInvoiceId), 
    [invoices, selectedInvoiceId]
  );

  const handleInspectionChange = (insId: string) => {
    const ins = inspections.find(i => i.id === insId);
    if (ins) {
      const prop = locations.find(l => l.id === ins.propertyId);
      const rates = formData.modifierRates || { 
        sqftRate: billingSettings.defaultSqftRate, 
        sqftThreshold: billingSettings.defaultSqftThreshold,
        distanceRate: billingSettings.defaultDistanceRate, 
        distanceThreshold: billingSettings.defaultDistanceThreshold,
        basementFee: billingSettings.defaultBasementFee, 
        crawlspaceFee: billingSettings.defaultCrawlspaceFee 
      };

      setFormData(prev => ({
        ...prev,
        inspectionId: insId,
        propertyId: ins.propertyId,
        contactId: ins.contactId,
        modifiers: {
          ...prev.modifiers!,
          sqft: prop?.squareFeet || 0,
          basement: !!prop?.metadata?.basementPresent,
          crawlspace: !!prop?.metadata?.crawlspacePresent,
          applySqftPremium: (prop?.squareFeet || 0) > rates.sqftThreshold
        }
      }));
      setIsInspectionDropdownOpen(false);
      setInspectionSearch('');
    } else {
      setFormData(prev => ({ ...prev, inspectionId: '', propertyId: '' }));
    }
  };

  useEffect(() => {
    if (initialInspectionId) {
      const ins = inspections.find(i => i.id === initialInspectionId);
      if (ins) {
        const existingInvoice = invoices.find(inv => inv.inspectionId === initialInspectionId);
        if (existingInvoice) {
          setSelectedInvoiceId(existingInvoice.id);
        } else {
          const prop = locations.find(l => l.id === ins.propertyId);
          setEditingId(null);
          setFormData({
            invoiceNumber: generateInvoiceNumber(),
            inspectionId: initialInspectionId,
            propertyId: ins.propertyId,
            contactId: ins.contactId,
            items: [],
            modifiers: {
              sqft: prop?.squareFeet || 0,
              distance: 0,
              basement: !!prop?.metadata?.basementPresent,
              crawlspace: !!prop?.metadata?.crawlspacePresent,
              applySqftPremium: (prop?.squareFeet || 0) > billingSettings.defaultSqftThreshold,
              applyDistancePremium: false
            },
            modifierRates: { 
              sqftRate: billingSettings.defaultSqftRate, 
              sqftThreshold: billingSettings.defaultSqftThreshold,
              distanceRate: billingSettings.defaultDistanceRate, 
              distanceThreshold: billingSettings.defaultDistanceThreshold,
              basementFee: billingSettings.defaultBasementFee, 
              crawlspaceFee: billingSettings.defaultCrawlspaceFee 
            },
            discounts: [],
            taxRate: businessProfile.defaultTaxRate || 0,
            status: 'draft'
          });
          setIsSidebarOpen(true);
        }
      }
    }
  }, [initialInspectionId, inspections, locations, billingSettings, invoices, businessProfile.defaultTaxRate]);

  const totals = useMemo(() => {
    const data = formData;
    const items = data.items || [];
    const subtotal = items.reduce((acc, item) => acc + item.price, 0);
    const rates: ModifierRates = data.modifierRates ? (data.modifierRates as ModifierRates) : {
      sqftRate: billingSettings.defaultSqftRate,
      sqftThreshold: billingSettings.defaultSqftThreshold,
      distanceRate: billingSettings.defaultDistanceRate,
      distanceThreshold: billingSettings.defaultDistanceThreshold,
      basementFee: billingSettings.defaultBasementFee,
      crawlspaceFee: billingSettings.defaultCrawlspaceFee
    };
    const mods = data.modifiers || { sqft: 0, distance: 0, applySqftPremium: false, applyDistancePremium: false, basement: false, crawlspace: false };
    
    let sqftPremium = 0;
    if (mods.applySqftPremium && mods.sqft > rates.sqftThreshold) {
      sqftPremium = (mods.sqft - rates.sqftThreshold) * rates.sqftRate;
    }
    
    let distancePremium = 0;
    if (mods.applyDistancePremium && mods.distance > rates.distanceThreshold) {
      distancePremium = (mods.distance - rates.distanceThreshold) * rates.distanceRate;
    }
    
    const basementFee = mods.basement ? rates.basementFee : 0;
    const crawlspaceFee = mods.crawlspace ? rates.crawlspaceFee : 0;
    
    const adjustmentsTotal = sqftPremium + distancePremium + basementFee + crawlspaceFee;
    const grossAmount = subtotal + adjustmentsTotal;
    
    let totalDiscount = 0;
    const discountBreakdown: { name: string, value: number }[] = [];
    (data.discounts || []).forEach(d => {
      let val = 0;
      if (d.type === 'Percentage') val = (grossAmount * (d.value / 100));
      else val = d.value;
      totalDiscount += val;
      discountBreakdown.push({ name: d.name, value: val });
    });
    
    const taxableAmount = Math.max(0, grossAmount - totalDiscount);
    const currentTaxRate = data.taxRate ?? businessProfile.defaultTaxRate ?? 0;
    const taxAmount = taxableAmount * (currentTaxRate / 100);
    const finalTotal = taxableAmount + taxAmount;
    
    return {
      subtotal, 
      sqftPremium, 
      distancePremium, 
      basementFee, 
      crawlspaceFee,
      adjustmentsTotal, 
      totalDiscount, 
      discountBreakdown,
      taxableAmount, 
      taxAmount, 
      taxRate: currentTaxRate,
      finalTotal: Math.round(finalTotal * 100) / 100
    };
  }, [formData, billingSettings, businessProfile.defaultTaxRate]);

  const handleSave = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!formData.contactId || !formData.invoiceNumber) return;
    setSaving(true);
    
    try {
      const payload = { 
        ...formData, 
        total: totals.finalTotal, 
        created_at: formData.createdAt || new Date().toISOString() 
      };
      
      const dbPayload = toSnakeCase(payload);
      
      if (editingId) {
        const { error } = await supabase.from('invoices').update(dbPayload).eq('id', editingId);
        if (error) throw error;
        setInvoices(prev => prev.map(inv => inv.id === editingId ? { ...inv, ...formData, total: totals.finalTotal } as Invoice : inv));
      } else {
        const { data, error } = await supabase.from('invoices').insert([dbPayload]).select().single();
        if (error) throw error;
        setInvoices(prev => [toCamelCase(data), ...prev]);
      }
      setIsSidebarOpen(false);
      if (onClearDraft) onClearDraft();
    } catch (err) { alert(`Sync failure: ${getErrorMessage(err)}`); } finally { setSaving(false); }
  };

  const handleStatusChange = async (newStatus: Invoice['status']) => {
    if (!selectedInvoiceId || !selectedInvoice) return;
    setIsUpdatingStatus(true);
    try {
      const { error } = await supabase.from('invoices')
        .update({ status: newStatus })
        .eq('id', selectedInvoiceId);

      if (error) throw error;
      
      setInvoices(prev => prev.map(inv => 
        inv.id === selectedInvoiceId ? { ...inv, status: newStatus } : inv
      ));
    } catch (err) {
      alert(`Status update failed: ${getErrorMessage(err)}`);
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleGenerateStripeLink = async () => {
    const targetId = selectedInvoiceId;
    if (!targetId || !selectedInvoice) return;

    setIsGeneratingStripe(true);
    try {
      const contact = getContact(selectedInvoice.contactId);
      const inspection = selectedInvoice.inspectionId ? getInspection(selectedInvoice.inspectionId) : null;
      const property = inspection ? getProperty(inspection.propertyId) : null;
      
      const payload = { 
        amount: Number(selectedInvoice.total), 
        customerEmail: contact?.email || '', 
        description: `Inspection: ${property?.address || 'Property'}`, 
        businessName: businessProfile.companyName || 'InspectorHub', 
        invoiceNumber: selectedInvoice.invoiceNumber 
      };

      const { data, error: invokeError } = await supabase.functions.invoke('stripe-checkout', { body: payload });
      if (invokeError) throw invokeError;
      
      const checkoutUrl = data?.url;
      if (!checkoutUrl) throw new Error("Gateway did not return a session URL.");
      
      const { error: dbError } = await supabase.from('invoices').update({ 
        stripe_checkout_url: checkoutUrl
      }).eq('id', targetId);
      
      if (dbError) throw dbError;
      
      setInvoices(prev => prev.map(inv => inv.id === targetId ? { ...inv, stripeCheckoutUrl: checkoutUrl } : inv));
    } catch (err) { 
      alert(getErrorMessage(err));
    } finally { 
      setIsGeneratingStripe(false); 
    }
  };

  const handleSendEmail = async () => {
    if (!selectedInvoice || !selectedInvoiceId) return;
    setIsSendingEmail(true);

    try {
      const contact = getContact(selectedInvoice.contactId);
      const inspection = selectedInvoice.inspectionId ? getInspection(selectedInvoice.inspectionId) : null;
      const property = inspection ? getProperty(inspection.propertyId) : null;

      if (!contact?.email) {
        throw new Error("Stakeholder email record is missing from the registry.");
      }

      // Ensure we have an archived PDF or ask the user to archive first for professionalism
      if (!selectedInvoice.pdfData) {
        if (!confirm("No archived PDF found for this record. Dispatch the email without a technical attachment?")) {
           setIsSendingEmail(false);
           return;
        }
      }

      const payload = {
        to: contact.email,
        customerName: contact.name || 'Valued Customer',
        invoiceNumber: selectedInvoice.invoiceNumber,
        amount: Number(selectedInvoice.total).toFixed(2),
        propertyAddress: property?.address || 'the scheduled property',
        businessName: businessProfile.companyName || 'InspectorHub Pro',
        paymentUrl: selectedInvoice.stripeCheckoutUrl || '',
        pdf_data: selectedInvoice.pdfData || null, // Sending the URL to the edge function
      };

      // Call the Edge Function
      const { data, error: invokeError } = await supabase.functions.invoke('smtp2go', { body: payload });
      
      if (invokeError) throw invokeError;
      if (data?.error) throw new Error(data.error);

      const timestamp = new Date().toISOString();
      const { error: dbError } = await supabase.from('invoices').update({ last_sent_at: timestamp }).eq('id', selectedInvoiceId);
      if (dbError) throw dbError;

      setInvoices(prev => prev.map(inv => inv.id === selectedInvoiceId ? { ...inv, lastSentAt: timestamp } : inv));
      alert(`Dispatch Successful: Invoice #${selectedInvoice.invoiceNumber} has been transmitted to ${contact.email}.`);
    } catch (err) {
      alert(`Dispatch Error: ${getErrorMessage(err)}`);
    } finally {
      setIsSendingEmail(false);
    }
  };

  const handleGeneratePDF = async () => {
    if (!selectedInvoice || !pdfTemplateRef.current) return;
    setIsGeneratingPDF(true);
    try {
      const element = pdfTemplateRef.current;
      const canvas = await html2canvas(element, { 
        scale: 2, 
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });
      const imgData = canvas.toDataURL('image/png');
      
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgProps = pdf.getImageProperties(imgData);
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
      
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      
      const pdfBlob = pdf.output('blob');
      const fileName = `invoice-${selectedInvoice.invoiceNumber}-${Date.now()}.pdf`;
      
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('Invoices')
        .upload(fileName, pdfBlob, {
          contentType: 'application/pdf',
          cacheControl: '3600',
          upsert: true
        });

      if (uploadError) throw new Error(`Storage upload failed: ${uploadError.message}`);

      const { data: { publicUrl } } = supabase.storage
        .from('Invoices')
        .getPublicUrl(fileName);

      const { error: dbError } = await supabase.from('invoices')
        .update({ pdf_data: publicUrl })
        .eq('id', selectedInvoice.id);

      if (dbError) throw new Error(`Database update failed: ${dbError.message}`);

      setInvoices(prev => prev.map(inv => inv.id === selectedInvoice.id ? { ...inv, pdfData: publicUrl } : inv));
      alert(`Document #${selectedInvoice.invoiceNumber} has been successfully archived in the registry.`);
      pdf.save(`Invoice_${selectedInvoice.invoiceNumber}.pdf`);
      
    } catch (err) {
      alert(`Archive Failure: ${getErrorMessage(err)}`);
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleDeleteInvoice = async (id: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    const invoiceToDelete = invoices.find(inv => inv.id === id);
    if (!invoiceToDelete) return;

    if (!confirm(`Are you sure you want to permanently delete invoice #${invoiceToDelete.invoiceNumber}? This will also purge its archived PDF from the registry.`)) return;
    
    try {
      // 1. Cleanup Storage Artifact if it exists
      if (invoiceToDelete.pdfData) {
        try {
          const urlParts = invoiceToDelete.pdfData.split('/');
          const fileName = urlParts[urlParts.length - 1];
          if (fileName) {
            await supabase.storage.from('Invoices').remove([fileName]);
            console.log(`Archived PDF artifact [${fileName}] purged from storage.`);
          }
        } catch (storageErr) {
          console.warn("Storage artifact cleanup failed. Proceeding with database record deletion.", storageErr);
        }
      }

      // 2. Remove database record
      const { error } = await supabase.from('invoices').delete().eq('id', id);
      if (error) throw error;

      setInvoices(prev => prev.filter(inv => inv.id !== id));
      if (selectedInvoiceId === id) setSelectedInvoiceId(null);
    } catch (err) {
      alert(`Deletion failure: ${getErrorMessage(err)}`);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch (err) {
      alert('Could not copy link to clipboard.');
    }
  };

  const filteredInvoices = useMemo(() => 
    invoices.filter(inv => {
      const contact = getContact(inv.contactId);
      const search = searchTerm.toLowerCase();
      return (
        inv.invoiceNumber.toLowerCase().includes(search) ||
        (contact?.name || '').toLowerCase().includes(search)
      );
    }).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
    [invoices, searchTerm, contacts]
  );

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'paid': return 'bg-green-50 text-green-600';
      case 'pending': return 'bg-amber-50 text-amber-600';
      case 'draft': return 'bg-blue-50 text-blue-600';
      case 'void': return 'bg-slate-50 text-slate-400';
      default: return 'bg-slate-50 text-slate-600';
    }
  };

  const filteredContacts = contacts.filter(c => 
    c.name.toLowerCase().includes(contactSearch.toLowerCase()) || 
    c.email.toLowerCase().includes(contactSearch.toLowerCase())
  );

  const filteredInspections = inspections.filter(i => {
    const prop = getProperty(i.propertyId);
    return prop?.address.toLowerCase().includes(inspectionSearch.toLowerCase()) || 
           i.date.includes(inspectionSearch);
  });

  const handleSavePredefinedDiscount = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!discountForm.name) return;
    setDiscountLibSaving(true);
    try {
      const dbPayload = toSnakeCase(discountForm);
      const { data, error } = await supabase.from('predefined_discounts').insert([dbPayload]).select().single();
      if (error) throw error;
      setBillingSettings(prev => ({
        ...prev,
        predefinedDiscounts: [...prev.predefinedDiscounts, toCamelCase(data)]
      }));
      setDiscountForm({ name: '', type: 'Percentage', value: 0 });
    } catch (err) { alert(getErrorMessage(err)); }
    finally { setDiscountLibSaving(false); }
  };

  const handleDeletePredefinedDiscount = async (id: string) => {
    if (!confirm('Purge this discount from the library?')) return;
    try {
      const { error } = await supabase.from('predefined_discounts').delete().eq('id', id);
      if (error) throw error;
      setBillingSettings(prev => ({
        ...prev,
        predefinedDiscounts: prev.predefinedDiscounts.filter(d => d.id !== id)
      }));
    } catch (err) { alert(getErrorMessage(err)); }
  };

  return (
    <div className="relative animate-in fade-in duration-300">
      {selectedInvoiceId && selectedInvoice ? (
        <div className="space-y-6 animate-in slide-in-from-right-2 pb-20">
          <div className="flex flex-col xl:flex-row xl:items-center justify-between bg-white p-4 rounded-xl border border-slate-200 shadow-sm gap-4">
            <div className="flex items-center gap-3">
              <button onClick={() => setSelectedInvoiceId(null)} className="p-2 hover:bg-slate-100 rounded-lg text-slate-400 transition-all"><ChevronLeft size={20} /></button>
              <div>
                <h3 className="font-bold text-slate-800">Invoice {selectedInvoice.invoiceNumber}</h3>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`text-[9px] font-black px-2 py-0.5 rounded-md uppercase tracking-wider ${getStatusColor(selectedInvoice.status)}`}>{selectedInvoice.status}</span>
                  <span className="text-[10px] text-slate-400 font-medium">• Created {new Date(selectedInvoice.createdAt).toLocaleDateString()}</span>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-4">
              <div className="flex bg-slate-50 p-1 rounded-xl border border-slate-100 items-center">
                {(['draft', 'pending', 'paid', 'void'] as const).map((status) => (
                  <button
                    key={status}
                    disabled={isUpdatingStatus || selectedInvoice.status === status}
                    onClick={() => handleStatusChange(status)}
                    className={`px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all flex items-center gap-1.5 ${
                      selectedInvoice.status === status
                        ? status === 'paid' ? 'bg-emerald-600 text-white shadow-md' :
                          status === 'pending' ? 'bg-amber-600 text-white shadow-md' :
                          status === 'draft' ? 'bg-blue-600 text-white shadow-md' :
                          'bg-slate-400 text-white shadow-md'
                        : 'text-slate-400 hover:text-slate-600'
                    }`}
                  >
                    {status === 'paid' && <Check size={12} />}
                    {status === 'pending' && <Clock size={12} />}
                    {status === 'draft' && <StickyNote size={12} />}
                    {status === 'void' && <Ban size={12} />}
                    {status}
                  </button>
                ))}
              </div>

              <div className="h-8 w-px bg-slate-100 hidden xl:block" />

              <div className="flex gap-2">
                <button disabled={isGeneratingPDF} onClick={handleGeneratePDF} className="px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-slate-50 transition-all flex items-center gap-2 shadow-sm">
                  {isGeneratingPDF ? <Loader2 size={14} className="animate-spin" /> : (selectedInvoice.pdfData ? <RefreshCw size={14} /> : <FileDown size={14} />)} {selectedInvoice.pdfData ? 'Re-archive' : 'Export & Archive'}
                </button>
                <button onClick={() => { setEditingId(selectedInvoiceId); setFormData({...selectedInvoice}); setIsSidebarOpen(true); }} className="px-4 py-2 bg-slate-900 text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-slate-800 transition-all flex items-center gap-2 shadow-sm"><Edit2 size={14} /> Modify</button>
                <button onClick={(e) => handleDeleteInvoice(selectedInvoice.id, e)} className="p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-all shadow-sm"><Trash2 size={18} /></button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            <div className="lg:col-span-3">
              <div ref={pdfTemplateRef} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden p-12">
                <div className="flex justify-between items-start mb-12">
                  <div>
                    <h1 className="text-2xl font-black text-slate-900 uppercase">{businessProfile.companyName}</h1>
                    <p className="text-[10px] text-slate-500 font-bold uppercase mt-2">{businessProfile.phone} • {businessProfile.email}</p>
                  </div>
                  <div className="text-right">
                    <h2 className="text-4xl font-black text-slate-100 mb-2">INVOICE</h2>
                    <p className="font-black text-slate-800 tracking-widest">#{selectedInvoice.invoiceNumber}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-10 mb-12 border-t border-slate-50 pt-12">
                  <div>
                    <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Bill To</h4>
                    <p className="font-black text-base text-slate-800">{getContact(selectedInvoice.contactId)?.name}</p>
                    <p className="font-medium text-slate-500">{getContact(selectedInvoice.contactId)?.email}</p>
                  </div>
                  {selectedInvoice.inspectionId && (
                    <div className="text-right">
                      <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Technical Session</h4>
                      <p className="font-black text-slate-800">{getProperty(selectedInvoice.propertyId || '')?.address}</p>
                    </div>
                  )}
                </div>
                <table className="w-full mb-12">
                  <thead><tr className="border-b-2 border-slate-100 text-left"><th className="py-2 text-[10px] font-black uppercase text-slate-400">Description</th><th className="py-2 text-[10px] font-black uppercase text-slate-400 text-right">Amount</th></tr></thead>
                  <tbody className="divide-y divide-slate-50">
                    {selectedInvoice.items.map((item, idx) => (
                      <tr key={`item-${idx}`}><td className="py-4 text-sm font-bold text-slate-700">{item.name}</td><td className="py-4 text-right font-black text-slate-900">${item.price.toLocaleString()}</td></tr>
                    ))}
                    
                    {selectedInvoice.modifiers?.applySqftPremium && selectedInvoice.modifiers.sqft > selectedInvoice.modifierRates?.sqftThreshold && (
                      <tr key="mod-sqft">
                        <td className="py-4 text-sm font-medium text-slate-600 italic">
                          Square Foot Adjustment (Area over {selectedInvoice.modifierRates.sqftThreshold} SF)
                        </td>
                        <td className="py-4 text-right font-bold text-slate-700">
                          +${((selectedInvoice.modifiers.sqft - selectedInvoice.modifierRates.sqftThreshold) * selectedInvoice.modifierRates.sqftRate).toLocaleString()}
                        </td>
                      </tr>
                    )}
                    {selectedInvoice.modifiers?.applyDistancePremium && selectedInvoice.modifiers.distance > selectedInvoice.modifierRates?.distanceThreshold && (
                      <tr key="mod-dist">
                        <td className="py-4 text-sm font-medium text-slate-600 italic">
                          Travel Surcharge (Miles over {selectedInvoice.modifierRates.distanceThreshold})
                        </td>
                        <td className="py-4 text-right font-bold text-slate-700">
                          +${((selectedInvoice.modifiers.distance - selectedInvoice.modifierRates.distanceThreshold) * selectedInvoice.modifierRates.distanceRate).toLocaleString()}
                        </td>
                      </tr>
                    )}
                    {selectedInvoice.modifiers?.basement && (
                      <tr key="mod-base">
                        <td className="py-4 text-sm font-medium text-slate-600 italic">Basement Access Adjustment</td>
                        <td className="py-4 text-right font-bold text-slate-700">
                          +${selectedInvoice.modifierRates?.basementFee.toLocaleString()}
                        </td>
                      </tr>
                    )}
                    {selectedInvoice.modifiers?.crawlspace && (
                      <tr key="mod-crawl">
                        <td className="py-4 text-sm font-medium text-slate-600 italic">Crawlspace Access Adjustment</td>
                        <td className="py-4 text-right font-bold text-slate-700">
                          +${selectedInvoice.modifierRates?.crawlspaceFee.toLocaleString()}
                        </td>
                      </tr>
                    )}

                    {selectedInvoice.discounts.map((d, idx) => (
                      <tr key={`disc-${idx}`}><td className="py-4 text-sm font-bold text-green-600">Discount: {d.name}</td><td className="py-4 text-right font-black text-green-600">-${d.value.toLocaleString()}</td></tr>
                    ))}
                  </tbody>
                </table>
                <div className="flex flex-col items-end gap-2 border-t border-slate-100 pt-8">
                  {selectedInvoice.taxRate > 0 && (
                    <div className="flex justify-between w-64 text-right mb-2">
                      <span className="text-[10px] font-bold uppercase text-slate-400">Tax ({selectedInvoice.taxRate}%)</span>
                      <span className="text-sm font-bold text-slate-600">
                        ${(selectedInvoice.total - (selectedInvoice.total / (1 + selectedInvoice.taxRate / 100))).toLocaleString()}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between w-64 text-right">
                    <span className="text-[10px] font-black uppercase text-slate-400">Total Due</span>
                    <span className="text-3xl font-black text-slate-900 tracking-tighter">${selectedInvoice.total.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              {businessProfile.stripeConnected && (
                <div className="bg-slate-900 p-6 rounded-2xl shadow-xl relative overflow-hidden group">
                  <h4 className="text-[10px] font-black uppercase text-indigo-400 tracking-[0.2em] mb-4 flex items-center gap-2"><ShieldCheck size={14} /> Financial Gateway</h4>
                  <div className="space-y-4 relative z-10">
                    {selectedInvoice.status.toLowerCase() === 'paid' ? (
                      <div className="p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl flex items-center gap-3">
                        <Check className="text-emerald-500" size={20} />
                        <p className="text-xs text-white font-bold uppercase">Paid & Reconciled</p>
                      </div>
                    ) : (
                      <>
                        {selectedInvoice.stripeCheckoutUrl ? (
                          <div className="space-y-2">
                            <button onClick={() => window.open(selectedInvoice.stripeCheckoutUrl, '_blank')} className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-[10px] font-black uppercase tracking-widest transition-all flex items-center justify-center gap-2 shadow-lg"><ExternalLink size={12} /> Pay with Stripe</button>
                            <button onClick={() => copyToClipboard(selectedInvoice.stripeCheckoutUrl!)} className="w-full py-2 bg-white/10 text-white rounded-xl text-[9px] font-bold uppercase tracking-widest flex items-center justify-center gap-2">
                              {copied ? <><Check size={12} /> Link Copied</> : <><Copy size={12} /> Copy Link</>}
                            </button>
                            <button onClick={handleGenerateStripeLink} disabled={isGeneratingStripe} className="w-full py-2 bg-white/5 text-slate-400 hover:text-white rounded-xl text-[9px] font-bold uppercase tracking-widest transition-all flex items-center justify-center gap-2 mt-2">
                              <RefreshCw size={12} className={isGeneratingStripe ? 'animate-spin' : ''} /> Refresh Gateway
                            </button>
                          </div>
                        ) : (
                          <button disabled={isGeneratingStripe} onClick={handleGenerateStripeLink} className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 shadow-lg disabled:opacity-50">
                            {isGeneratingStripe ? <Loader2 className="animate-spin" size={14} /> : <Zap size={14} />} Enable Online Pay
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}

              {businessProfile.smtpEnabled && (
                <div className={`p-6 rounded-2xl border transition-all duration-300 relative overflow-hidden group shadow-sm ${selectedInvoice.stripeCheckoutUrl ? 'bg-emerald-50/30 border-emerald-200' : 'bg-white border-slate-200'}`}>
                  <div className="flex justify-between items-center mb-4">
                    <h4 className="text-[10px] font-black uppercase text-emerald-600 tracking-[0.2em] flex items-center gap-2"><SendHorizontal size={14} /> Email Dispatch</h4>
                    {selectedInvoice.stripeCheckoutUrl && (
                      <span className="text-[8px] font-black bg-emerald-600 text-white px-1.5 py-0.5 rounded-full animate-pulse uppercase tracking-tighter">Ready</span>
                    )}
                  </div>
                  <div className="space-y-4 relative z-10">
                    <p className="text-[10px] text-slate-500 font-medium leading-relaxed">
                      Dispatch this invoice directly to the client's inbox via SMTP2GO. {selectedInvoice.pdfData ? 'Includes the generated PDF as an attachment.' : 'Archive the PDF first to include a physical document copy.'}
                    </p>
                    <button 
                      disabled={isSendingEmail} 
                      onClick={handleSendEmail} 
                      className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 shadow-lg disabled:opacity-50 transition-all active:scale-[0.98]"
                    >
                      {isSendingEmail ? <Loader2 className="animate-spin" size={14} /> : <Mail size={14} />} Send via Email
                    </button>
                    {selectedInvoice.lastSentAt && (
                      <div className="pt-2 border-t border-emerald-100 mt-2">
                        <p className="text-[9px] text-emerald-600 font-bold text-center uppercase tracking-tighter flex items-center justify-center gap-1">
                          <MailCheck size={10} /> Last Dispatched: {new Date(selectedInvoice.lastSentAt).toLocaleString()}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {selectedInvoice.pdfData && (
                <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm relative overflow-hidden">
                  <h4 className="text-[10px] font-black uppercase text-slate-400 tracking-[0.2em] mb-4 flex items-center gap-2"><FileText size={14} /> Archived Copy</h4>
                  <button 
                    onClick={() => {
                      const link = document.createElement('a');
                      link.href = selectedInvoice.pdfData!;
                      link.target = "_blank";
                      link.download = `Invoice_${selectedInvoice.invoiceNumber}.pdf`;
                      link.click();
                    }}
                    className="w-full py-3 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 transition-all"
                  >
                    <Download size={14} /> Download Archived
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div><h3 className="text-xl font-bold text-slate-800 uppercase tracking-tight">Billing Ledger</h3><p className="text-sm text-slate-500 font-medium">Financial management and receivables.</p></div>
            <div className="flex items-center gap-3 w-full md:w-auto">
              <div className="relative flex-1 md:w-72"><Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} /><input type="text" placeholder="Search invoices..." className="w-full pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium outline-none shadow-sm" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} /></div>
              <button 
                onClick={() => setIsDiscountLibraryOpen(true)}
                className="p-2 bg-white border border-slate-200 text-slate-500 hover:text-indigo-600 rounded-lg transition-all shadow-sm"
                title="Manage Discount Library"
              >
                <Settings2 size={20} />
              </button>
              <button onClick={() => { 
                setEditingId(null); 
                setSelectedInvoiceId(null);
                setFormData({
                  invoiceNumber: generateInvoiceNumber(),
                  items: [],
                  status: 'draft',
                  modifiers: { sqft: 0, distance: 0, basement: false, crawlspace: false, applySqftPremium: false, applyDistancePremium: false },
                  modifierRates: { 
                    sqftRate: billingSettings.defaultSqftRate, 
                    sqftThreshold: billingSettings.defaultSqftThreshold,
                    distanceRate: billingSettings.defaultDistanceRate, 
                    distanceThreshold: billingSettings.defaultDistanceThreshold,
                    basementFee: billingSettings.defaultBasementFee, 
                    crawlspaceFee: billingSettings.defaultCrawlspaceFee 
                  },
                  discounts: [],
                  taxRate: businessProfile.defaultTaxRate || 0,
                });
                setIsSidebarOpen(true); 
              }} className="bg-slate-900 text-white px-5 py-2 rounded-lg text-sm font-semibold shadow-sm hover:bg-slate-800 transition-all flex items-center gap-2 whitespace-nowrap"><Plus size={16} /> New Invoice</button>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <table className="w-full text-left">
              <thead><tr className="bg-slate-50/50 border-b border-slate-200"><th className="px-6 py-4 text-[10px] font-bold text-slate-500 uppercase tracking-widest">Registry ID</th><th className="px-6 py-4 text-[10px] font-bold text-slate-500 uppercase tracking-widest">Recipient</th><th className="px-6 py-4 text-[10px] font-bold text-slate-500 uppercase tracking-widest text-right">Balance</th><th className="px-6 py-4 text-[10px] font-bold text-slate-500 uppercase tracking-widest text-center">Status</th><th className="px-6 py-4 text-[10px] font-bold text-slate-500 uppercase tracking-widest text-right">Actions</th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {filteredInvoices.map(inv => (
                  <tr key={inv.id} onClick={() => setSelectedInvoiceId(inv.id)} className="hover:bg-slate-50/80 transition-colors cursor-pointer group">
                    <td className="px-6 py-4 font-bold text-slate-800 text-sm">#{inv.invoiceNumber}</td>
                    <td className="px-6 py-4 text-xs font-semibold text-slate-600">{getContact(inv.contactId)?.name || 'Unknown'}</td>
                    <td className="px-6 py-4 text-right font-black text-slate-900">${inv.total?.toLocaleString()}</td>
                    <td className="px-6 py-4 text-center"><span className={`text-[9px] font-black px-2 py-0.5 rounded-md uppercase tracking-wider ${getStatusColor(inv.status)}`}>{inv.status}</span></td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={(e) => handleDeleteInvoice(inv.id, e)} className="p-2 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all" title="Delete Invoice"><Trash2 size={16} /></button>
                        <div className="inline-flex items-center justify-center p-2 text-slate-400 group-hover:text-blue-600 group-hover:bg-blue-50 rounded-lg transition-all">
                          <ChevronRight size={18} />
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* CRUD Sidebar */}
      <div className={`fixed inset-0 z-[300] ${isSidebarOpen ? 'visible' : 'invisible'}`}>
        <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm transition-opacity duration-200" onClick={() => setIsSidebarOpen(false)} />
        <div className={`absolute inset-y-0 right-0 w-full max-w-3xl bg-white shadow-xl transition-transform duration-300 ${isSidebarOpen ? 'translate-x-0' : 'translate-x-full'} flex flex-col`}>
          <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50">
            <h3 className="font-bold text-slate-800 text-xs uppercase tracking-widest">{editingId ? 'Modify Ledger Entry' : 'Register New Ledger Entry'}</h3>
            <button onClick={() => setIsSidebarOpen(false)} className="p-2 hover:bg-white rounded-lg text-slate-400 transition-all"><X size={20} /></button>
          </div>
          <form onSubmit={handleSave} className="flex-1 p-8 space-y-10 overflow-y-auto no-scrollbar">
            
            <section className="space-y-4">
              <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest border-b pb-2 flex items-center gap-2"><User size={14} /> Stakeholder Information</h4>
              <div className="relative" ref={inspectionDropdownRef}>
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest block mb-1.5">Inspection Search</label>
                <div 
                  className={`flex items-center p-3 bg-slate-50 border ${isInspectionDropdownOpen ? 'border-blue-500 ring-4 ring-blue-500/5' : 'border-slate-200'} rounded-xl cursor-text transition-all`}
                  onClick={() => setIsInspectionDropdownOpen(true)}
                >
                  <Calendar size={16} className="text-slate-400 mr-2" />
                  <input 
                    className="bg-transparent border-none outline-none text-sm font-bold w-full"
                    placeholder="Search by property address or date..."
                    value={isInspectionDropdownOpen ? inspectionSearch : (getProperty(getInspection(formData.inspectionId || '')?.propertyId || '')?.address || '')}
                    onChange={(e) => { setInspectionSearch(e.target.value); setIsInspectionDropdownOpen(true); }}
                  />
                  {formData.inspectionId && !isInspectionDropdownOpen && <button onClick={(e) => { e.stopPropagation(); setFormData({...formData, inspectionId: '', propertyId: ''}); }} className="ml-auto p-1 hover:bg-slate-200 rounded-md"><X size={14} className="text-slate-500" /></button>}
                </div>
                {isInspectionDropdownOpen && (
                  <div className="absolute z-20 mt-2 w-full bg-white border border-slate-200 rounded-2xl shadow-2xl overflow-hidden max-h-[300px] overflow-y-auto no-scrollbar animate-in zoom-in-95 duration-200">
                    <button onClick={() => { setFormData({...formData, inspectionId: '', propertyId: ''}); setIsInspectionDropdownOpen(false); }} className="w-full text-left p-4 hover:bg-slate-50 border-b border-slate-50 text-xs font-bold text-slate-400 uppercase tracking-widest">No Linked Session</button>
                    {filteredInspections.length > 0 ? filteredInspections.map(i => (
                      <button 
                        key={i.id} 
                        type="button"
                        onClick={() => handleInspectionChange(i.id)}
                        className="w-full text-left p-4 hover:bg-slate-50 border-b border-slate-50 flex items-center gap-3 transition-colors"
                      >
                        <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center text-slate-400 shrink-0"><MapPin size={14} /></div>
                        <div className="min-w-0">
                          <p className="text-sm font-bold text-slate-800 truncate">{getProperty(i.propertyId)?.address}</p>
                          <p className="text-[10px] font-medium text-slate-400 truncate">{i.date} • {getContact(i.contactId)?.name}</p>
                        </div>
                      </button>
                    )) : <div className="p-8 text-center text-slate-400 text-xs italic">No inspections found matching "{inspectionSearch}"</div>}
                  </div>
                )}
              </div>
              
              <div className="relative" ref={contactDropdownRef}>
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest block mb-1.5">Contact Search</label>
                <div 
                  className={`flex items-center p-3 bg-slate-50 border ${isContactDropdownOpen ? 'border-blue-500 ring-4 ring-blue-500/5' : 'border-slate-200'} rounded-xl cursor-text transition-all`}
                  onClick={() => setIsContactDropdownOpen(true)}
                >
                  <Search size={16} className="text-slate-400 mr-2" />
                  <input 
                    className="bg-transparent border-none outline-none text-sm font-bold w-full"
                    placeholder="Search by name or email..."
                    value={isContactDropdownOpen ? contactSearch : (getContact(formData.contactId || '')?.name || '')}
                    onChange={(e) => { setContactSearch(e.target.value); setIsContactDropdownOpen(true); }}
                  />
                  {formData.contactId && !isContactDropdownOpen && <button onClick={(e) => { e.stopPropagation(); setFormData({...formData, contactId: ''}); }} className="ml-auto p-1 hover:bg-slate-200 rounded-md"><X size={14} className="text-slate-500" /></button>}
                </div>
                {isContactDropdownOpen && (
                  <div className="absolute z-20 mt-2 w-full bg-white border border-slate-200 rounded-2xl shadow-2xl overflow-hidden max-h-[300px] overflow-y-auto no-scrollbar animate-in zoom-in-95 duration-200">
                    {filteredContacts.length > 0 ? filteredContacts.map(c => (
                      <button 
                        key={c.id} 
                        type="button"
                        onClick={() => { setFormData({...formData, contactId: c.id}); setIsContactDropdownOpen(false); setContactSearch(''); }}
                        className="w-full text-left p-4 hover:bg-slate-50 border-b border-slate-50 flex items-center gap-3 transition-colors"
                      >
                        <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 shrink-0"><User size={14} /></div>
                        <div className="min-w-0">
                          <p className="text-sm font-bold text-slate-800 truncate">{c.name}</p>
                          <p className="text-[10px] font-medium text-slate-400 truncate">{c.email}</p>
                        </div>
                      </button>
                    )) : <div className="p-8 text-center text-slate-400 text-xs italic">No contacts found matching "{contactSearch}"</div>}
                  </div>
                )}
              </div>

              
            </section>

            <section className="space-y-4">
              <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest border-b pb-2 flex items-center gap-2"><Briefcase size={14} /> Catalog items</h4>
              <div className="bg-slate-50 rounded-2xl p-6 space-y-4">
                <div className="flex gap-2">
                  <select className="flex-1 p-3 bg-white border border-slate-200 rounded-xl text-sm font-bold outline-none focus:ring-2 focus:ring-blue-500" value={servicePickerId} onChange={(e) => setServicePickerId(e.target.value)}>
                    <option value="">Choose from catalog...</option>
                    {services.map(s => <option key={s.id} value={s.id}>{s.name} (${s.basePrice})</option>)}
                  </select>
                  <button 
                    type="button" 
                    onClick={() => {
                      const service = services.find(s => s.id === servicePickerId);
                      if (service) {
                        setFormData(prev => ({
                          ...prev,
                          items: [...(prev.items || []), { serviceId: service.id, name: service.name, price: service.basePrice }]
                        }));
                        setServicePickerId('');
                      }
                    }} 
                    className="px-6 py-2 bg-slate-900 text-white rounded-xl text-xs font-black uppercase hover:bg-slate-800 transition-all flex items-center gap-2"
                  >
                    <Plus size={14} /> Add
                  </button>
                </div>
                <div className="space-y-2">
                  {(formData.items || []).map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between p-4 bg-white border border-slate-100 rounded-xl shadow-sm animate-in fade-in slide-in-from-left-2 duration-200">
                      <div>
                        <p className="text-sm font-bold text-slate-800">{item.name}</p>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-0.5">${item.price.toLocaleString()}</p>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-1">
                          <DollarSign size={10} className="text-slate-300" />
                          <input 
                            type="number" 
                            className="w-20 p-1 bg-slate-50 border-none rounded text-xs font-black text-slate-800 text-right"
                            value={item.price}
                            onChange={(e) => {
                              const newItems = [...(formData.items || [])];
                              newItems[idx].price = parseFloat(e.target.value) || 0;
                              setFormData({...formData, items: newItems});
                            }}
                          />
                        </div>
                        <button type="button" onClick={() => setFormData(prev => ({...prev, items: (prev.items || []).filter((_, i) => i !== idx)}))} className="text-slate-300 hover:text-red-500 transition-colors p-1"><Trash2 size={16} /></button>
                      </div>
                    </div>
                  ))}
                  {(formData.items || []).length === 0 && <div className="py-8 text-center text-[10px] font-bold text-slate-400 uppercase tracking-widest">No Line Items Defined</div>}
                </div>
              </div>
            </section>

            <section className="space-y-4">
              <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest border-b pb-2 flex items-center gap-2"><Calculator size={14} /> Global Adjustments</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                
                <div className="p-6 bg-slate-50 rounded-2xl border border-slate-200 space-y-4">
                  <label className="flex items-center gap-3 cursor-pointer group">
                    <input type="checkbox" className="w-5 h-5 rounded-lg border-slate-300 text-blue-600 focus:ring-blue-500" checked={formData.modifiers?.applySqftPremium} onChange={(e)=>setFormData(prev => ({...prev, modifiers: {...prev.modifiers!, applySqftPremium: e.target.checked}}))} />
                    <span className="text-[11px] font-black text-slate-700 uppercase tracking-wider group-hover:text-blue-600 transition-colors">SF Premium</span>
                  </label>
                  {formData.modifiers?.applySqftPremium && (
                    <div className="space-y-4 pt-2 border-t border-slate-200/50 animate-in fade-in duration-300">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-1">Area (SF)</label>
                          <input type="number" className="w-full p-2.5 bg-white border border-slate-200 rounded-xl text-xs font-bold" value={formData.modifiers?.sqft} onChange={(e)=>setFormData(prev => ({...prev, modifiers: {...prev.modifiers!, sqft: parseFloat(e.target.value) || 0}}))} />
                        </div>
                        <div>
                          <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-1">Rate / SF</label>
                          <input type="number" step="0.01" className="w-full p-2.5 bg-white border border-slate-200 rounded-xl text-xs font-bold" value={formData.modifierRates?.sqftRate} onChange={(e)=>setFormData(prev => ({...prev, modifierRates: {...prev.modifierRates!, sqftRate: parseFloat(e.target.value) || 0}}))} />
                        </div>
                      </div>
                      <p className="text-[8px] font-bold text-slate-400 uppercase tracking-tighter italic">Threshold: {formData.modifierRates?.sqftThreshold} SF</p>
                    </div>
                  )}
                </div>

                <div className="p-6 bg-slate-50 rounded-2xl border border-slate-200 space-y-4">
                  <label className="flex items-center gap-3 cursor-pointer group">
                    <input type="checkbox" className="w-5 h-5 rounded-lg border-slate-300 text-amber-600 focus:ring-amber-500" checked={formData.modifiers?.applyDistancePremium} onChange={(e)=>setFormData(prev => ({...prev, modifiers: {...prev.modifiers!, applyDistancePremium: e.target.checked}}))} />
                    <span className="text-[11px] font-black text-slate-700 uppercase tracking-wider group-hover:text-amber-600 transition-colors">Distance Premium</span>
                  </label>
                  {formData.modifiers?.applyDistancePremium && (
                    <div className="space-y-4 pt-2 border-t border-slate-200/50 animate-in fade-in duration-300">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-1">Miles</label>
                          <input type="number" className="w-full p-2.5 bg-white border border-slate-200 rounded-xl text-xs font-bold" value={formData.modifiers?.distance} onChange={(e)=>setFormData(prev => ({...prev, modifiers: {...prev.modifiers!, distance: parseFloat(e.target.value) || 0}}))} />
                        </div>
                        <div>
                          <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-1">Rate / Mile</label>
                          <input type="number" step="0.01" className="w-full p-2.5 bg-white border border-slate-200 rounded-xl text-xs font-bold" value={formData.modifierRates?.distanceRate} onChange={(e)=>setFormData(prev => ({...prev, modifierRates: {...prev.modifierRates!, distanceRate: parseFloat(e.target.value) || 0}}))} />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="p-6 bg-slate-50 rounded-2xl border border-slate-200 space-y-4">
                  <label className="flex items-center gap-3 cursor-pointer group">
                    <input type="checkbox" className="w-5 h-5 rounded-lg border-slate-300 text-slate-600 focus:ring-slate-500" checked={formData.modifiers?.basement} onChange={(e)=>setFormData(prev => ({...prev, modifiers: {...prev.modifiers!, basement: e.target.checked}}))} />
                    <span className="text-[11px] font-black text-slate-700 uppercase tracking-wider group-hover:text-slate-900 transition-colors">Basement Fee</span>
                  </label>
                  {formData.modifiers?.basement && (
                    <div className="pt-2 border-t border-slate-200/50 animate-in fade-in duration-300">
                      <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-1">Fixed Charge ($)</label>
                      <input type="number" className="w-full p-2.5 bg-white border border-slate-200 rounded-xl text-xs font-bold" value={formData.modifierRates?.basementFee} onChange={(e)=>setFormData(prev => ({...prev, modifierRates: {...prev.modifierRates!, basementFee: parseFloat(e.target.value) || 0}}))} />
                    </div>
                  )}
                </div>

                <div className="p-6 bg-slate-50 rounded-2xl border border-slate-200 space-y-4">
                  <label className="flex items-center gap-3 cursor-pointer group">
                    <input type="checkbox" className="w-5 h-5 rounded-lg border-slate-300 text-slate-600 focus:ring-slate-500" checked={formData.modifiers?.crawlspace} onChange={(e)=>setFormData(prev => ({...prev, modifiers: {...prev.modifiers!, crawlspace: e.target.checked}}))} />
                    <span className="text-[11px] font-black text-slate-700 uppercase tracking-wider group-hover:text-slate-900 transition-colors">Crawlspace Fee</span>
                  </label>
                  {formData.modifiers?.crawlspace && (
                    <div className="pt-2 border-t border-slate-200/50 animate-in fade-in duration-300">
                      <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-1">Fixed Charge ($)</label>
                      <input type="number" className="w-full p-2.5 bg-white border border-slate-200 rounded-xl text-xs font-bold" value={formData.modifierRates?.crawlspaceFee} onChange={(e)=>setFormData(prev => ({...prev, modifierRates: {...prev.modifierRates!, crawlspaceFee: parseFloat(e.target.value) || 0}}))} />
                    </div>
                  )}
                </div>
              </div>
            </section>

            <section className="space-y-4">
              <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest border-b pb-2 flex items-center gap-2"><Tag size={14} /> Discount Protocol</h4>
              <div className="bg-slate-50 rounded-2xl p-6 space-y-4">
                <div className="flex gap-2">
                  <select 
                    className="flex-1 p-3 bg-white border border-slate-200 rounded-xl text-sm font-bold outline-none"
                    onChange={(e) => {
                      const disc = billingSettings.predefinedDiscounts.find(d => d.id === e.target.value);
                      if (disc) {
                        setFormData(prev => ({
                          ...prev,
                          discounts: [...(prev.discounts || []), { ...disc }]
                        }));
                      }
                      e.target.value = '';
                    }}
                  >
                    <option value="">Apply from library...</option>
                    {billingSettings.predefinedDiscounts.map(d => (
                      <option key={d.id} value={d.id}>{d.name} ({d.type === 'Percentage' ? `${d.value}%` : `$${d.value}`})</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  {(formData.discounts || []).map((d, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-white border border-emerald-50 rounded-xl">
                      <div className="flex items-center gap-2">
                        <Tag size={12} className="text-emerald-500" />
                        <span className="text-xs font-bold text-slate-800">{d.name}</span>
                        <span className="text-[9px] font-black text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded uppercase">{d.type === 'Percentage' ? `${d.value}%` : `$${d.value}`}</span>
                      </div>
                      <button type="button" onClick={() => setFormData(prev => ({...prev, discounts: (prev.discounts || []).filter((_, i) => i !== idx)}))} className="text-slate-300 hover:text-red-500 transition-colors"><X size={14} /></button>
                    </div>
                  ))}
                  {(formData.discounts || []).length === 0 && <div className="py-4 text-center text-[9px] font-bold text-slate-400 uppercase">No Discounts Applied</div>}
                </div>
              </div>
            </section>

            <section className="bg-slate-900 rounded-3xl p-8 text-white shadow-2xl relative overflow-hidden">
              <div className="absolute top-0 right-0 p-8 text-white/5 -rotate-12 pointer-events-none"><Calculator size={120} /></div>
              <div className="flex justify-between items-end mb-8 relative z-10"><div><p className="text-[10px] font-black text-blue-400 uppercase tracking-[0.2em] mb-1.5">Projected Balance</p><h3 className="text-5xl font-black tracking-tighter">${totals.finalTotal.toLocaleString()}</h3></div></div>
              <div className="space-y-3 pt-6 border-t border-white/10 text-xs font-medium relative z-10">
                <div className="flex justify-between"><span className="text-slate-400 font-bold uppercase text-[9px] tracking-widest">Base Subtotal</span><span className="text-slate-200 font-black">${totals.subtotal.toLocaleString()}</span></div>
                {totals.sqftPremium > 0 && <div className="flex justify-between"><span className="text-slate-400 font-bold uppercase text-[9px] tracking-widest">SF Adjustment</span><span className="text-slate-200 font-black">+${totals.sqftPremium.toLocaleString()}</span></div>}
                {totals.distancePremium > 0 && <div className="flex justify-between"><span className="text-slate-400 font-bold uppercase text-[9px] tracking-widest">Distance Surcharge</span><span className="text-slate-200 font-black">+${totals.distancePremium.toLocaleString()}</span></div>}
                {totals.basementFee > 0 && <div className="flex justify-between"><span className="text-slate-400 font-bold uppercase text-[9px] tracking-widest">Basement Registry</span><span className="text-slate-200 font-black">+${totals.basementFee.toLocaleString()}</span></div>}
                {totals.crawlspaceFee > 0 && <div className="flex justify-between"><span className="text-slate-400 font-bold uppercase text-[9px] tracking-widest">Crawlspace Registry</span><span className="text-slate-200 font-black">+${totals.crawlspaceFee.toLocaleString()}</span></div>}
                {totals.totalDiscount > 0 && <div className="flex justify-between"><span className="text-emerald-400 font-bold uppercase text-[9px] tracking-widest">Applied Discounts</span><span className="text-emerald-400 font-black">-${totals.totalDiscount.toLocaleString()}</span></div>}
                {totals.taxAmount > 0 && <div className="flex justify-between"><span className="text-indigo-400 font-bold uppercase text-[9px] tracking-widest">Tax Provision ({totals.taxRate}%)</span><span className="text-indigo-400 font-black">+${totals.taxAmount.toLocaleString()}</span></div>}
                <div className="flex justify-between pt-4 border-t border-white/20 mt-4"><span className="text-slate-400 font-bold uppercase text-[10px]">Net Final Balance</span><span className="text-white text-xl font-black">${totals.finalTotal.toLocaleString()}</span></div>
              </div>
            </section>
          </form>
          <div className="p-8 border-t border-slate-100 flex gap-4 bg-white">
            <button type="button" onClick={() => setIsSidebarOpen(false)} className="flex-1 py-4 text-xs font-bold text-slate-500 hover:bg-slate-50 rounded-2xl border border-slate-200 transition-all uppercase tracking-widest">Abort</button>
            <button disabled={saving} onClick={() => handleSave()} className="flex-[2] py-4 bg-slate-900 text-white font-bold text-xs rounded-2xl hover:bg-slate-800 transition-all flex items-center justify-center gap-2 shadow-xl uppercase tracking-widest">
              {saving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />} Commit Registry Entry
            </button>
          </div>
        </div>
      </div>

      {isDiscountLibraryOpen && (
        <div className="fixed inset-0 z-[400] flex items-center justify-center p-6">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity" onClick={() => setIsDiscountLibraryOpen(false)} />
          <div className="bg-white w-full max-w-2xl rounded-3xl shadow-2xl relative overflow-hidden animate-in zoom-in-95 duration-300 flex flex-col max-h-[80vh]">
            <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-indigo-600 text-white rounded-lg"><Tag size={20} /></div>
                <div>
                  <h3 className="font-semibold text-slate-800 text-sm uppercase tracking-widest">Discount Taxonomy</h3>
                  <p className="text-[10px] text-slate-400 font-medium uppercase tracking-wider">Promotion & Adjustment Registry</p>
                </div>
              </div>
              <button onClick={() => setIsDiscountLibraryOpen(false)} className="p-2 hover:bg-white rounded-full text-slate-400 transition-colors"><X size={20} /></button>
            </div>
            
            <div className="p-8 overflow-y-auto space-y-8 no-scrollbar">
              <form onSubmit={handleSavePredefinedDiscount} className="p-6 bg-slate-50 rounded-2xl border border-slate-100 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-widest block mb-1.5">Promotion Label</label>
                    <input 
                      required 
                      className="w-full p-2.5 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-800 outline-none focus:ring-1 focus:ring-blue-500" 
                      value={discountForm.name} 
                      onChange={(e) => setDiscountForm({...discountForm, name: e.target.value})} 
                      placeholder="e.g. First-Time Homebuyer"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-widest block mb-1.5">Adjustment Value</label>
                    <input 
                      required 
                      type="number"
                      className="w-full p-2.5 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-800 outline-none" 
                      value={discountForm.value} 
                      onChange={(e) => setDiscountForm({...discountForm, value: parseFloat(e.target.value) || 0})} 
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-widest block mb-1.5">Value Type</label>
                    <select 
                      className="w-full p-2.5 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-800 outline-none"
                      value={discountForm.type}
                      onChange={(e) => setDiscountForm({...discountForm, type: e.target.value as any})}
                    >
                      <option value="Percentage">Percentage (%)</option>
                      <option value="Amount">Flat Amount ($)</option>
                    </select>
                  </div>
                </div>
                <div className="flex justify-end pt-2">
                  <button disabled={discountLibSaving} type="submit" className="bg-slate-900 text-white px-6 py-2.5 rounded-xl text-xs font-semibold flex items-center gap-2 shadow-sm hover:bg-slate-800">
                    {discountLibSaving ? <Loader2 className="animate-spin" size={14} /> : <Plus size={14} />} Register Promotion
                  </button>
                </div>
              </form>

              <div className="space-y-3">
                <h4 className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest border-b border-slate-100 pb-2">Active Library Entries</h4>
                <div className="grid grid-cols-1 gap-2">
                  {billingSettings.predefinedDiscounts.length > 0 ? billingSettings.predefinedDiscounts.map(d => (
                    <div key={d.id} className="p-4 bg-white border border-slate-100 rounded-2xl flex items-center justify-between group hover:border-slate-200 transition-all">
                      <div className="flex items-center gap-4">
                        <div className="p-2 bg-emerald-50 text-emerald-600 rounded-lg"><Tag size={14} /></div>
                        <div>
                          <p className="text-sm font-bold text-slate-700">{d.name}</p>
                          <p className="text-[10px] font-black text-emerald-600 uppercase">{d.type === 'Percentage' ? `${d.value}% Discount` : `$${d.value} Adjustment`}</p>
                        </div>
                      </div>
                      <button onClick={() => handleDeletePredefinedDiscount(d.id!)} className="p-2 text-slate-300 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"><Trash2 size={16} /></button>
                    </div>
                  )) : (
                    <div className="py-12 text-center text-slate-400 italic text-xs">No promotions indexed.</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Invoices;